import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Phone, Globe, ExternalLink, Building2 } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { useAuth } from "@/context/AuthContext";
import { COMPANY } from "@/lib/company";
import { cn } from "@/lib/utils";

export default function Contact() {
  const { user } = useAuth();
  const navigate = useNavigate();
  return (
    <div className={cn("text-white", !user && "min-h-screen bg-am-bg")} data-testid="contact-page">
      {!user && (
        <header className="sticky top-0 z-40 flex items-center justify-between border-b border-am-border bg-am-bg/90 px-4 py-3 backdrop-blur-xl">
          <button onClick={() => navigate("/")} data-testid="contact-public-logo" className="flex items-center gap-2"><Logo size={30} /><span className="font-mark text-base font-extrabold tracking-tight">AURAMAX</span></button>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate("/login")} data-testid="contact-public-login" className="rounded-full px-3 py-1.5 text-sm font-semibold text-am-text2 hover:text-white">Log in</button>
            <button onClick={() => navigate("/join")} data-testid="contact-public-join" className="rounded-full aura-gradient px-4 py-1.5 text-sm font-bold text-black">Join AuraMax</button>
          </div>
        </header>
      )}
      <main className="mx-auto max-w-2xl px-4 py-5 pb-24 md:px-6">
        {user && <button onClick={() => navigate(-1)} data-testid="contact-back" className="mb-4 flex items-center gap-1 text-sm text-am-text2 hover:text-white"><ArrowLeft className="h-4 w-4" /> Back</button>}

        <section className="relative overflow-hidden rounded-3xl border border-am-border bg-am-surface p-6 sm:p-8 animate-[fadeIn_.4s_ease]">
          <span className="absolute inset-x-0 top-0 h-px aura-gradient opacity-80" />
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-am-text3"><Building2 className="h-4 w-4" /> AuraMax HQ</div>
          <h1 className="mt-2 font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl">{COMPANY.brand}</h1>
          <p className="text-sm text-am-text2" data-testid="contact-legal-name">{COMPANY.legalName}</p>

          <div className="mt-6">
            <div className="text-xs font-bold uppercase tracking-wide text-am-text3">Headquarters contact</div>
            <a href={COMPANY.phoneTel} data-testid="contact-phone" aria-label={`Call AuraMax HQ at ${COMPANY.phoneDisplay}`} className="mt-2 inline-flex items-center gap-3 rounded-full border border-am-border-strong bg-am-surface2 px-6 py-3 text-xl font-extrabold text-white transition-colors hover:bg-white/5 sm:text-2xl">
              <Phone className="h-6 w-6 text-aura-cyan" /> {COMPANY.phoneDisplay}
            </a>
            <div className="mt-2 font-mono text-sm text-am-text2" data-testid="contact-phone-intl">{COMPANY.phoneIntl}</div>
            <p className="mt-2 text-sm text-am-text2" data-testid="contact-availability">{COMPANY.availability}</p>
          </div>
        </section>

        <section className="mt-4 rounded-3xl border border-am-border bg-am-surface p-5" data-testid="contact-inquiries">
          <h2 className="text-base font-bold text-white md:text-lg">General business inquiries</h2>
          <p className="mt-2 text-sm leading-relaxed text-am-text2">Support, partnerships, press and administrative matters all reach {COMPANY.legalName} through the same number above.</p>
        </section>

        <section className="mt-4 rounded-3xl border border-am-border bg-am-surface p-5" data-testid="contact-channels">
          <h2 className="text-base font-bold text-white md:text-lg">Official channels</h2>
          <div className="mt-3 flex flex-col gap-2 text-sm">
            <a href={COMPANY.website} target="_blank" rel="noopener noreferrer" data-testid="contact-website" className="inline-flex items-center gap-2 text-aura-cyan hover:underline"><Globe className="h-4 w-4" /> {COMPANY.website.replace("https://", "")} <ExternalLink className="h-3.5 w-3.5" /></a>
            <a href={COMPANY.xUrl} target="_blank" rel="noopener noreferrer" data-testid="contact-x" className="inline-flex items-center gap-2 text-aura-cyan hover:underline"><span className="font-bold">𝕏</span> Official X account <ExternalLink className="h-3.5 w-3.5" /></a>
          </div>
        </section>

        <p className="mt-6 text-center text-xs leading-relaxed text-am-text3" data-testid="contact-footnote">This is a business contact line — not an investment hotline or financial advisory service. It is never used for account recovery or verification; AuraMax will never ask for passwords, codes or wallet recovery phrases by phone.</p>
      </main>
    </div>
  );
}
